/**
 * Périmètre de lecture d'un utilisateur — PUR. Remplace le filtre « un seul campus
 * OU une seule école » de `useGovAccess().scopeFilter` : un Doyen peut diriger
 * plusieurs écoles, un enseignant enseigner dans plusieurs, un responsable
 * académique suppléer (intérim) dans une autre.
 *
 *   · mode dynamique avec affectations : les affectations ACTIVES (bornes d'intérim
 *     respectées) font foi ; une affectation « tout l'établissement » = aucune
 *     restriction ;
 *   · sinon : rattachement historique `profiles.campus_id` / `school_id`
 *     (comportement antérieur, le campus PRIME sur l'école).
 */

/**
 * @param {{ mode?: string, assignments?: {scope_type:string, scope_id:string|null, is_active?:boolean, delegate_start?:string|null, delegate_end?:string|null}[],
 *           legacy?: { campusId?: string|null, schoolId?: string|null }, now?: Date }} p
 * @returns {{ tenantWide: boolean, schoolIds: string[], campusIds: string[], source: 'assignments'|'legacy'|'none' }}
 */
export function effectiveScopeSets({ mode = 'legacy', assignments = [], legacy = {}, now = new Date() } = {}) {
  if (mode === 'dynamic' && assignments.length > 0) {
    const schoolIds = new Set();
    const campusIds = new Set();
    let tenantWide = false;
    let autre = false; // périmètre d'un autre niveau (programme, classe…) : non traduisible ici
    for (const a of assignments) {
      if (a.is_active === false) continue;
      if (a.delegate_start && new Date(a.delegate_start) > now) continue;
      if (a.delegate_end && new Date(a.delegate_end) < now) continue;
      if (a.scope_type === 'tenant') tenantWide = true;
      else if (a.scope_type === 'school' && a.scope_id) schoolIds.add(a.scope_id);
      else if (a.scope_type === 'campus' && a.scope_id) campusIds.add(a.scope_id);
      else autre = true;
    }
    // Un périmètre qu'on ne sait pas traduire ne RESTREINT pas ici (le filtre est un
    // confort d'écran ; la garde de sécurité est la RLS) — jamais d'écran vide à tort.
    if (tenantWide || autre) return { tenantWide: true, schoolIds: [], campusIds: [], source: 'assignments' };
    if (schoolIds.size || campusIds.size) return { tenantWide: false, schoolIds: [...schoolIds], campusIds: [...campusIds], source: 'assignments' };
  }
  if (legacy.campusId) return { tenantWide: false, schoolIds: [], campusIds: [legacy.campusId], source: 'legacy' };
  if (legacy.schoolId) return { tenantWide: false, schoolIds: [legacy.schoolId], campusIds: [], source: 'legacy' };
  return { tenantWide: true, schoolIds: [], campusIds: [], source: 'none' };
}

/**
 * Applique le périmètre à une requête supabase-js. `campusField` / `schoolField`
 * désignent les colonnes de la table interrogée.
 */
export function applyScopeSets(query, sets, { campusField = 'campus_id', schoolField = 'school_id' } = {}) {
  if (!sets || sets.tenantWide) return query;
  const { schoolIds, campusIds } = sets;
  if (schoolIds.length && campusIds.length) {
    return query.or(`${schoolField}.in.(${schoolIds.join(',')}),${campusField}.in.(${campusIds.join(',')})`);
  }
  if (schoolIds.length) return schoolIds.length === 1 ? query.eq(schoolField, schoolIds[0]) : query.in(schoolField, schoolIds);
  if (campusIds.length) return campusIds.length === 1 ? query.eq(campusField, campusIds[0]) : query.in(campusField, campusIds);
  return query;
}
